import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { useOrganizationSaml } from "@/api/auth/useOrganizationSaml";
import { SpinningLoader } from "@/components/SpinningLoader";
import { PATHS } from "@/constants/paths";
import { assignSafeLocation } from "@/lib/safe-redirect";
import { clearLocalStorage } from "@/lib/utils";

import { AuthLayout } from "./AuthLayout";

// NOTE: NSAI-7736 — an organization's own sign-in link
// (`/auth/sso/<organizationUuid>`): straight to that organization's IdP, for a
// member who has no email or domain to type on the SSO page. Anything the
// server cannot route (unknown organization, SAML not configured, a malformed
// uuid) lands on the regular SSO page with the "not found" notice. A refused
// sign-in comes back to `/auth/sso?error=…`, never here, so this page cannot
// bounce a person back to the IdP on its own.
const OrganizationSso = (): React.JSX.Element => {
  const navigate = useNavigate();
  const { t } = useTranslation("auth");
  const { organizationUuid } = useParams<{ organizationUuid: string }>();

  const { data, isLoading, isError } = useOrganizationSaml(organizationUuid);

  // NOTE: One redirect per visit — StrictMode runs effects twice in dev, and
  // each IdP redirect is a fresh AuthnRequest.
  const hasHandledRef = useRef(false);

  useEffect(() => {
    clearLocalStorage();
  }, []);

  useEffect(() => {
    if ((isLoading && Boolean(organizationUuid)) || hasHandledRef.current) {
      return;
    }

    hasHandledRef.current = true;

    if (!isError && data) {
      try {
        assignSafeLocation(data.samlUrl);
        return;
      } catch {
        // NOTE: An unsafe target is treated like a missing configuration.
      }
    }

    toast.error(t("sso.saml_config_not_found"));
    navigate(PATHS.AUTH.SSO, { replace: true });
  }, [data, isError, isLoading, navigate, organizationUuid, t]);

  return (
    <AuthLayout>
      <div
        className="flex flex-col gap-4 items-center justify-center min-h-[300px]"
        data-testid="organization-sso-redirecting"
      >
        <SpinningLoader />
        <p className="text-muted-foreground">
          {t("sso.checking_organization")}
        </p>
      </div>
    </AuthLayout>
  );
};

export default OrganizationSso;
